// Italian (tu) copy for /preview-first-uploads, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// The second pass is always "gli originali" (never "qualità piena"). The uploader's own labels are in English,
// so they're quoted as it shows them.
import type { PreviewFirstCopy } from "./copy.en";

const it: PreviewFirstCopy = {
  seo: {
    title: "Caricamenti con anteprima, con tutti i piani | Photohawk",
    description:
      "Novità nell'uploader web di Photohawk, con tutti i piani: di ogni foto viene caricata prima una piccola anteprima, così la tua galleria può andare online, vendere ed essere condivisa in pochi minuti mentre gli originali arrivano in background.",
  },
  hero: {
    badge: "Novità",
    eyebrow: "Nell'uploader web · Tutti i piani",
    title: "La tua galleria online in pochi minuti,",
    titleHighlight: "non in ore",
    subtitle:
      "Ora l'uploader web invia prima una piccola anteprima di ogni foto. La tua galleria può andare online con queste anteprime, così gli acquirenti possono trovare, acquistare e condividere le loro foto mentre gli originali continuano a caricarsi in background.",
    ctaPrimary: "Apri l'uploader",
    ctaSecondary: "Scopri come funziona",
    uploaderLink: "Scopri tutte le novità dell'uploader web",
  },
  steps: {
    heading: "Come funziona",
    subtitle: "L'anteprima prima è nell'uploader web, con tutti i piani. Quando aggiungi le foto, mostra quanto richiede il caricamento in ciascun modo e scegli «Previews first, originals later».",
    stepLabel: "Passo {n}",
    items: [
      {
        id: "add",
        title: "Aggiungi le tue foto",
        body: "Trascina le foto o un'intera cartella, come fai sempre. Il tuo browser crea una piccola anteprima di ognuna, direttamente sul tuo computer.",
      },
      {
        id: "previews",
        title: "Le anteprime vanno su per prime",
        body: "Le anteprime sono leggere, quindi si caricano in fretta, anche con una connessione lenta sul posto.",
      },
      {
        id: "live",
        title: "La tua galleria va online",
        body: "Pubblicala appena le anteprime sono caricate, oppure spunta «Publish the gallery as soon as the previews are up» e succede da solo. Gli acquirenti possono sfogliare, cercare, acquistare e condividere subito.",
      },
      {
        id: "full",
        title: "Seguono gli originali",
        body: "Gli originali continuano a caricarsi in background, nelle stesse foto. Download e stampe usano sempre gli originali.",
      },
    ],
    timeline: {
      label: "Un caricamento di esempio, dall'inizio alla fine",
      previews: "Anteprime",
      previewsTime: "minuti",
      live: "Galleria online",
      full: "Originali",
      fullTime: "in background",
    },
  },
  buyers: {
    heading: "Cosa vedono gli acquirenti",
    subtitle: "Niente di nuovo da imparare. Gli acquirenti trovano la galleria che conoscono, solo prima.",
    items: [
      {
        id: "protected",
        title: "Con filigrana e protette, come sempre",
        body: "Gli acquirenti sfogliano le stesse anteprime con filigrana di sempre, e la protezione delle anteprime funziona allo stesso modo se la tua galleria la usa.",
      },
      {
        id: "search",
        title: "Si trovano subito",
        body: "La ricerca per selfie e per pettorale funziona sulle anteprime, così le persone trovano le loro foto appena la galleria è online.",
      },
      {
        id: "delivery",
        title: "Originali, consegnati in automatico",
        body: "Un acquisto o un download gratuito non viene mai consegnato come anteprima. Se qualcuno acquista o scarica una foto prima che arrivi il suo originale, l'originale passa in testa alla coda e la persona lo riceve appena è pronto.",
      },
    ],
  },
  strip: {
    heading: "Tieni d'occhio tutto mentre carichi",
    body: "Mentre si caricano anteprime e originali, una striscia in cima all'uploader mostra a che punto è la tua galleria: se è già online, quanti originali mancano e quando una foto acquistata è passata in testa. Quando la galleria è online, mostra anche quante persone l'hanno visitata negli ultimi 30 minuti, e i proprietari e gli amministratori dell'area di lavoro vedono i nuovi ordini dall'inizio del caricamento.",
    exampleLabel: "Esempio",
    live: "Online con le anteprime",
    orders: "3 ordini da quando hai iniziato",
    visitors: "42 visitatori negli ultimi 30 min",
    left: "Mancano 1.250 originali",
    jumped: "Gli originali di 2 foto ordinate sono passati in testa",
  },
  faq: {
    heading: "Domande",
    items: [
      {
        q: "Devo tenere aperto l'uploader?",
        a: "È meglio, finché gli originali non sono finiti; la striscia mostra quanti ne mancano. Se lo chiudi prima, non si perde niente. La tua galleria resta online con le anteprime e, la prossima volta che apri quella galleria nell'uploader, ti dice quante foto stanno ancora aspettando. Aggiungi di nuovo la stessa cartella (anche più tardi, da casa) e vengono inviati solo gli originali mancanti.",
      },
      {
        q: "E se qualcuno acquista una foto prima che arrivi il suo originale?",
        a: "Può comunque acquistarla. Finché l'uploader è in funzione, l'originale di quella foto passa in testa alla coda. L'email dell'ordine aspetta il file e parte entro pochi minuti dal suo arrivo, così l'acquirente non riceve mai l'anteprima per errore. Se l'uploader è stato chiuso, l'ordine aspetta finché non aggiungi di nuovo la cartella. L'uploader ti indica quali foto sta aspettando un acquirente, e quelle partono per prime.",
      },
      {
        q: "Funziona anche per le gallerie con download gratuito?",
        a: "Sì. Le persone possono guardare, cercare e scaricare appena le anteprime sono caricate, e un download non è mai l'anteprima. Se qualcuno scarica una foto prima che arrivi il suo originale, l'originale passa in testa alla coda: il download indica che l'originale è in caricamento e diventa un download funzionante appena arriva il file, e un download inviato per email parte in quel momento. Ogni download conta una sola volta.",
      },
      {
        q: "La ricerca per selfie e per pettorale funziona sulle anteprime?",
        a: "Sì. Volti e numeri di pettorale vengono rilevati sulle anteprime, quindi la ricerca funziona appena sono caricate. Finché l'uploader è aperto, invia anche primi piani più nitidi presi dai tuoi originali per aiutare la ricerca.",
      },
      {
        q: "Quali piani la includono?",
        a: "Tutti i piani, anche Lite. Se la tua area di lavoro usava già l'anteprima prima, quelle foto vengono completate normalmente: aggiungi di nuovo la stessa cartella e i loro originali vengono inviati, e i relativi ordini e download arrivano comunque.",
      },
      {
        q: "Costa qualcosa in più?",
        a: "No. È inclusa in tutti i piani, e ogni foto conta una sola volta: l'anteprima e il suo originale sono la stessa foto. L'unica differenza è che la tua connessione invia un po' più di dati in totale (circa il 5% in più).",
      },
      {
        q: "Funziona con l'FTP e Photohawk Studio?",
        a: "Per ora no. L'anteprima prima fa parte dell'uploader web. Le foto inviate via FTP o da Photohawk Studio vengono caricate direttamente come originali, in un solo passaggio, come prima.",
      },
      {
        q: "È attiva per impostazione predefinita?",
        a: "No. Ogni volta che aggiungi foto, l'uploader web mostra quanto richiederanno con e senza anteprima prima, ed è selezionato l'invio diretto degli originali. Scegli «Previews first, originals later» quando vuoi la galleria online prima; l'uploader ricorda la tua scelta per la volta successiva. Con una risoluzione di caricamento più bassa («Large» o «Web») vengono caricati solo file ridimensionati, quindi l'anteprima prima non si applica.",
      },
    ],
  },
  cta: {
    heading: "Provala al tuo prossimo caricamento",
    body: "L'anteprima prima è nell'uploader web, con tutti i piani. Aggiungi le tue foto, scegli l'anteprima prima e la tua galleria può essere online in pochi minuti.",
    button: "Apri l'uploader",
  },
};

export default it;
